/**
 * Cut free text to a summary length, at a word boundary — never a flat character slice.
 *
 * `claude-kit/rules/invariants.md`'s "Table columns need a deliberate strategy for long or
 * variable-length content": a long, free-text field (a clinical note's `content`, up to
 * `MAX_NOTE_CONTENT_LENGTH` — 20,000 characters, `shared/src/encounterRequests.ts`) is summarized
 * by default with an explicit control to read the rest, and the cut lands on a word boundary so it
 * reads as a truncation rather than a typo. `NoteCard` (`routes/patients/tabs/NotesTimeline.tsx`)
 * is the one caller; pulled out here, pure and DOM-free, so the boundary logic has its own test
 * rather than only being exercised by rendering.
 */
export interface Summary {
  /** The text to show by default. Equal to the original when it already fits. */
  readonly summary: string;
  /** Whether `summary` is shorter than the original — the caller shows "Show more" only then. */
  readonly truncated: boolean;
}

export function summarizeText(text: string, maxChars: number): Summary {
  if (text.length <= maxChars) {
    return { summary: text, truncated: false };
  }

  const slice = text.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(' ');
  // No space at all within the slice — an unbroken run at least `maxChars` long (a concatenated id,
  // a hash) — cut flat rather than returning the whole original untruncated.
  const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;

  return { summary: cut.trimEnd(), truncated: true };
}
